/**
 * Navigator Tuning · Config versions (feature 130). Draft / live / archived list, publish
 * of the draft and rollback to an archived version, both with a required justification
 * and gated by admin:tune-navigator. Layout: design/Publish.dc.html.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  useCanTuneNavigator,
  useConfigVersions,
  useDiscardDraft,
  usePublishDraft,
  useRollbackConfigVersion,
} from '../hooks/useConfigVersions';
import { isStaleDraftError } from '../services/navigatorTuningApi';
import type { ConfigVersion } from '../types/navigator-tuning.type';
import { changesLabel, paramChanges } from './parameters';

type Action = { kind: 'publish' | 'rollback'; id: string };

const BADGE: Record<ConfigVersion['status'], { label: string; className: string }> = {
  live: { label: 'Live', className: 'bg-[#e3efec] text-[#014d41]' },
  draft: { label: 'Draft', className: 'bg-[#fdecdc] text-[#9a3412]' },
  archived: { label: 'Archived', className: 'bg-[#eceef3] text-[#3d4659]' },
};

const BTN =
  'inline-flex h-10 items-center justify-center rounded-lg px-3.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bquest-green focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY = 'bg-bquest-green font-bold text-white hover:bg-[#014d41]';
const OUTLINE_GREEN = 'border border-bquest-green bg-white font-bold text-bquest-green hover:bg-[#e3efec]';
const OUTLINE_NEUTRAL = 'border border-[#d9d3c7] bg-white font-semibold text-[#1b2232]';

const NO_PERMISSION = 'Requires the admin:tune-navigator permission.';

const tagOf = (v: ConfigVersion | undefined) =>
  !v ? '—' : v.versionNumber ? `v${v.versionNumber}` : 'Draft';

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

export function ConfigVersionsScreen() {
  const { data: versions = [], isLoading, isError } = useConfigVersions();
  const canTune = useCanTuneNavigator();
  const publish = usePublishDraft();
  const rollback = useRollbackConfigVersion();
  const discard = useDiscardDraft();

  const [action, setAction] = useState<Action | null>(null);
  const [note, setNote] = useState('');
  const [ack, setAck] = useState(false);
  const [published, setPublished] = useState<ConfigVersion | null>(null);
  const [error, setError] = useState<string | null>(null);

  const byId = (id: string | null) => versions.find((v) => v.id === id);
  const live = versions.find((v) => v.status === 'live');
  const target = action ? byId(action.id) : undefined;
  const pending = publish.isPending || rollback.isPending;
  const blocked = !canTune || !note.trim() || !ack || pending || !target;

  const open = (next: Action) => {
    setAction(next);
    setNote('');
    setAck(false);
    setPublished(null);
    setError(null);
  };

  const confirm = async () => {
    if (!action || !target) return;
    setError(null);
    try {
      const justification = note.trim();
      const result =
        action.kind === 'publish'
          ? await publish.mutateAsync({ id: target.id, revision: target.revision, justification })
          : await rollback.mutateAsync({ id: target.id, justification });
      setPublished(result);
      setAction(null);
    } catch (e) {
      setError(
        isStaleDraftError(e)
          ? 'Another admin changed the draft (or published it) since you loaded it. The list was reloaded: review it and try again.'
          : 'Could not save. Try again.',
      );
    }
  };

  const onDiscard = (draft: ConfigVersion) => {
    if (!window.confirm('Discard the draft? Its changes are lost.')) return;
    discard.mutate(draft.id);
    if (action?.id === draft.id) setAction(null);
  };

  if (isLoading) return <p className="text-[#56607a]">Loading config versions…</p>;
  if (isError) return <p role="alert">Failed to load config versions.</p>;

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
      <section aria-labelledby="config-versions-heading">
        <h2 id="config-versions-heading" className="font-recoleta text-[30px] font-semibold text-[#1b2232]">
          Config versions
        </h2>
        <p className="mt-1.5 mb-4 text-sm text-[#56607a]">
          One global config drives every new plan. Each plan records the version it was generated with;
          delivered plans never change.
        </p>
        <ol className="overflow-hidden rounded-[14px] border border-border-subtle bg-white">
          {versions.map((v) => {
            const badge = BADGE[v.status];
            return (
              <li
                key={v.id}
                aria-label={tagOf(v)}
                className="flex flex-wrap items-center gap-4 border-t border-[#efeae1] px-5 py-4 first:border-t-0"
              >
                <span className="w-11 text-lg font-bold text-[#1b2232]">{tagOf(v)}</span>
                <div className="flex min-w-0 flex-1 basis-[280px] flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-[#1b2232]">
                      {v.justification ?? (v.status === 'draft' ? 'Unpublished changes' : '—')}
                    </span>
                    <span data-badge className={`rounded-full px-2 py-0.5 text-xs font-bold ${badge.className}`}>
                      {badge.label}
                    </span>
                  </div>
                  <span className="text-sm text-[#56607a]">
                    {v.status === 'draft'
                      ? `Draft · saved by ${v.createdBy?.email ?? 'unknown'} · from ${tagOf(byId(v.basedOnVersionId))}`
                      : `Published ${formatDate(v.publishedAt)} by ${v.publishedBy?.email ?? 'system'}`}
                  </span>
                  {v.status === 'draft' && live && (
                    <span className="text-[13px] text-[#3d4659]">
                      <span>{changesLabel(paramChanges(live.params, v.params).length)} vs. {tagOf(live)}</span>
                      {' · '}
                      <Link
                        to="/admin/navigator-tuning/review-changes"
                        className="font-semibold text-bquest-green hover:underline"
                      >
                        Review changes
                      </Link>
                    </span>
                  )}
                  {v.rolledBackFromVersionId && (
                    <span className="text-[13px] text-[#3d4659]">
                      Rolled back from {tagOf(byId(v.rolledBackFromVersionId))}
                    </span>
                  )}
                </div>
                {v.status !== 'live' && (
                  <div className="flex flex-col items-end gap-1">
                    <div className="flex gap-2">
                      {v.status === 'draft' && (
                        <button type="button" className={`${BTN} ${OUTLINE_NEUTRAL}`} onClick={() => onDiscard(v)}>
                          Discard
                        </button>
                      )}
                      <button
                        type="button"
                        className={`${BTN} ${v.status === 'draft' ? PRIMARY : OUTLINE_GREEN}`}
                        disabled={!canTune}
                        title={canTune ? undefined : NO_PERMISSION}
                        onClick={() => open({ kind: v.status === 'draft' ? 'publish' : 'rollback', id: v.id })}
                      >
                        {v.status === 'draft' ? 'Publish…' : 'Roll back to this'}
                      </button>
                    </div>
                    {!canTune && <span className="text-xs text-[#56607a]">{NO_PERMISSION}</span>}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </section>

      <aside aria-label="Publish" className="space-y-4">
        {error && (
          <div role="alert" className="rounded-lg border border-[#f3c6a5] bg-[#fdecdc] p-3 text-sm text-[#9a3412]">
            {error}
          </div>
        )}
        {action && target && (
          <div className="flex flex-col gap-4 rounded-[14px] border border-border-subtle bg-white p-5">
            <h3 className="text-lg font-bold text-[#1b2232]">
              {action.kind === 'publish' ? 'Publish draft as live config' : `Roll back to ${tagOf(target)}`}
            </h3>
            <p className="text-sm text-[#56607a]">
              {action.kind === 'publish'
                ? `Replaces ${tagOf(live)} for every new plan, registered and guest. Plans already delivered keep their version.`
                : `Publishes a new version with ${tagOf(target)}'s parameters. Versions in between stay archived.`}
            </p>
            <div className="flex flex-col gap-2">
              <label htmlFor="config-version-note" className="block text-sm font-semibold text-[#1b2232]">
                Why this change <span className="font-normal text-[#9a3412]">(required)</span>
              </label>
              <Textarea
                id="config-version-note"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Too many empty Senior Living results in FL"
                className="resize-y rounded-lg border-[#d9d3c7] px-3 py-2.5 text-[#1b2232]"
              />
            </div>
            <label className="flex items-start gap-2.5 text-sm text-[#1b2232]">
              <input
                type="checkbox"
                className="mt-0.5 h-[18px] w-[18px] shrink-0 accent-bquest-green"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
              />
              <span>I reviewed the impact on recent plans. New plans for every user will use this config.</span>
            </label>
            <button
              type="button"
              className="h-11 w-full rounded-lg bg-bquest-green text-sm font-bold text-white hover:bg-[#014d41] disabled:cursor-not-allowed disabled:border disabled:border-[#d9d3c7] disabled:bg-[#f4f0e9] disabled:text-[#8a90a0]"
              disabled={blocked}
              onClick={confirm}
            >
              {action.kind === 'publish' ? 'Publish draft' : 'Roll back'}
            </button>
          </div>
        )}
        {published && (
          <div
            role="status"
            className="flex flex-col gap-2.5 rounded-[14px] border border-border-subtle bg-white p-5"
          >
            <h3 className="text-lg font-bold text-[#014d41]">{tagOf(published)} is live</h3>
            <p className="text-sm text-[#56607a]">
              New plans now use it. Plans already delivered keep their original version. You can roll back from
              this list at any time.
            </p>
            <button type="button" className={`${BTN} ${OUTLINE_NEUTRAL} self-start`} onClick={() => setPublished(null)}>
              Done
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
